"use client";

import { Briefcase, ChevronDown, FolderKanban, Mail, Package, Truck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/** Static preview of `/jobs/[id]` — payment rollup, project link, materials timeline (not live data). */
export function MarketingJobsListMockup({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "pointer-events-none select-none bg-background p-4 text-foreground sm:p-5",
        className
      )}
      aria-hidden
    >
      <p className="text-xs font-medium text-muted-foreground">← All jobs</p>

      <div className="mt-4 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Briefcase className="h-5 w-5" aria-hidden />
        </div>
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-lg font-semibold leading-snug text-foreground">Kitchen refresh – Oak Lane</p>
            <Badge variant="secondary" className="shrink-0 text-[10px]">
              Scheduled
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">Sarah Martinez · 142 Oak Lane, Maple Grove</p>
          <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <FolderKanban className="h-3.5 w-3.5 shrink-0 text-primary/80" aria-hidden />
            Project:{" "}
            <span className="font-medium text-primary">Smith basement remodel</span>
          </p>
        </div>
      </div>

      <Card className="mt-5">
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2 space-y-0 pb-2">
          <div className="space-y-1">
            <CardTitle className="text-base">Payments (linked quotes)</CardTitle>
            <CardDescription>
              <span className="font-medium text-amber-800 dark:text-amber-300">Partially paid</span>
              {" · "}
              47% paid
            </CardDescription>
          </div>
          <div className="inline-flex h-8 shrink-0 items-center rounded-md px-2 text-xs text-muted-foreground">
            <ChevronDown className="mr-1 h-3.5 w-3.5" aria-hidden />
            Show breakdown
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-2xl font-bold tabular-nums">$4,250</span>
              <span className="text-sm text-muted-foreground">of $8,942</span>
            </div>
            <div className="h-2 rounded-full bg-muted">
              <div className="h-2 w-[47%] rounded-full bg-emerald-500" />
            </div>
          </div>

          <div className="rounded-lg border border-border/70 bg-muted/15 p-3 space-y-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-primary">Kitchen backsplash &amp; cabinet refresh</span>
                  <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
                    Primary
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">25% deposit collected · balance due at completion</p>
              </div>
              <div className="inline-flex h-9 shrink-0 items-center rounded-md border border-primary/30 px-3 text-sm text-foreground">
                <Mail className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                Request balance payment
              </div>
            </div>
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-base font-bold tabular-nums">$4,250</span>
                <span className="text-xs text-muted-foreground">of $8,942</span>
              </div>
              <div className="h-2 rounded-full bg-muted">
                <div className="h-2 w-[47%] rounded-full bg-emerald-500" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="mt-5 grid w-full max-w-md grid-cols-2 gap-1 rounded-lg bg-muted p-1">
        <div className="rounded-md bg-background px-3 py-1.5 text-center text-sm font-medium shadow-sm">Overview</div>
        <div className="rounded-md px-3 py-1.5 text-center text-sm text-muted-foreground">Materials &amp; timeline</div>
      </div>

      <Card className="mt-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Materials &amp; timeline</CardTitle>
          <CardDescription>Track orders and sub visits against the job start date.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-3 rounded-lg border border-border/70 bg-muted/10 p-3">
            <Package className="mt-0.5 h-4 w-4 shrink-0 text-blue-700" aria-hidden />
            <div className="min-w-0 flex-1 space-y-1">
              <p className="text-sm font-medium text-foreground">Cabinet hardware kit</p>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200">Ordered</Badge>
                <span>Expected Mar 22, 2026</span>
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-lg border border-border/70 bg-muted/10 p-3">
            <Truck className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden />
            <div className="min-w-0 flex-1 space-y-1">
              <p className="text-sm font-medium text-foreground">Drywall crew · Phase 1</p>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge className="bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100">In transit</Badge>
                <span>Sub visit Mar 18, 2026</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <p className="mt-4 text-center text-[11px] text-muted-foreground">
        Jobs list also groups work under <span className="font-medium text-foreground">Projects</span> when a remodel spans
        multiple trades.
      </p>
    </div>
  );
}
